import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX } from 'react-icons/hi';
import { BsRobot, BsPerson } from 'react-icons/bs';
import { trackEvent } from '../../utils/analytics';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'School', href: '#learning-paths' },
  { label: 'Engineering', href: '#engineering' },
  { label: 'FAQ', href: '#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user'));
      if (stored) setUser(stored);
    } catch {}
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    trackEvent("landing_nav_clicked", { label: href });
    if (href.startsWith('#')) {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogout = () => {
    trackEvent("logout", { source: "landing_navbar" });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setMobileOpen(false);
  };

  const displayName = user?.username || user?.name || user?.email?.split('@')[0];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass-strong shadow-2xl shadow-black/20' : 'bg-transparent'
        }`}
      >
        <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-brand-500/30 transition-all duration-300">
                <BsRobot className="text-xl text-white" />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-brand-400 to-accent-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <span className="text-2xl font-bold">
                <span className="text-white">Zen</span>
                <span className="gradient-text">QB</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="px-5 py-2.5 text-sm font-medium text-gray-300 hover:text-white rounded-xl hover:bg-white/5 transition-all duration-300"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 glass rounded-xl">
                    <BsPerson className="text-brand-400" />
                    <span className="text-sm font-medium text-gray-200">{displayName}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => trackEvent("login_started", { source: "landing_navbar" })}
                    className="px-5 py-2.5 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-300"
                  >
                    Log in
                  </Link>
                  <Link to="/signup" onClick={() => trackEvent("signup_started", { source: "landing_navbar" })} className="btn-primary text-sm !px-6 !py-2.5">
                    <span className="relative z-10">Start Free</span>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white"
            >
              {mobileOpen ? <HiX className="text-2xl" /> : <HiMenuAlt3 className="text-2xl" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 pt-20 bg-dark-500/98 backdrop-blur-2xl md:hidden"
          >
            <div className="flex flex-col items-center gap-2 p-8">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="w-full py-4 text-lg font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-2xl transition-all"
                >
                  {link.label}
                </button>
              ))}
              <div className="w-full h-px bg-white/10 my-4" />
              {user ? (
                <>
                  <div className="flex items-center gap-2 py-4 text-lg font-medium text-gray-200">
                    <BsPerson className="text-brand-400" />
                    {displayName}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full py-4 text-lg font-medium text-gray-400 hover:text-white text-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => { setMobileOpen(false); trackEvent("login_started", { source: "landing_navbar_mobile" }); }}
                    className="w-full py-4 text-lg font-medium text-gray-300 hover:text-white text-center"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => { setMobileOpen(false); trackEvent("signup_started", { source: "landing_navbar_mobile" }); }}
                    className="btn-primary w-full text-center text-lg mt-2"
                  >
                    <span className="relative z-10">Start Free</span>
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
